// A stand-in viewport for UI work and tests while the wgpu wasm build isn't available. It speaks
// the same API (api.ts) and draws each structure as a soft ellipse at its mesh bounds on a 2D
// canvas: enough to exercise picking, selection, isolation, search, labels and the camera. It also
// reads `sim` headers and colours `overlay`-keyed structures by value at the `setTime` time. It reports
// API 0.2; with `?mockApi=0.3` it reports 0.3 and draws a cells run's layers (mockCells.ts), and, as the
// engine's 0.3 does, lists the sim layers it draws in `ready.layers` and refuses any other. It loads
// anatomy packs only, as its `ready.packs` says, and refuses a connectome or tissue pack by its
// `pack.json`, so the app never offers it a tissue view. A pack that composes (`extends`) brings the
// structures of the packs it extends, as the engine loads them; its own hemisphere surfaces are left
// out, as the app's catalogue leaves them, so the base's parcels stand for the cortex.

import type { Colormap, Command, Mode, Orbit, Reply, Target, Vec3, Viewport, ViewportEvent, ViewportOptions } from './api.ts';
import { ANCHOR_BEHIND, ANCHOR_IN_VIEW, ANCHOR_STRIDE, ANCHOR_VISIBLE } from './api.ts';
import { eyeOf, lookAt, multiply, perspective, PRESETS, project, UP, type Mat4 } from './mat.ts';
import { colormapStops, hexRgb, sampleStops, type Stops } from '../ui/scales.ts';
import { loadPlate, paintPlate, paintSurface, stillCamera, type Framing, type Plate, type StillSpec } from './still.ts';
import { MockCells, type MockLayer } from './mockCells.ts';
import type { CellsHeader } from '../data/sims/types.ts';

declare global {
  interface Window {
    /** Set before the app starts by scripts/parity.ts: a design plate stands in for the picture. */
    __baStill?: StillSpec;
  }
}

interface Item {
  id: string;
  system: string;
  parent: string | null;
  context: boolean;
  colour: string;
  min: Vec3;
  max: Vec3;
  centre: Vec3;
  radius: number;
}

interface RawStructure {
  id: string;
  system: string;
  parent: string | null;
  mesh: string | null;
  tract: string | null;
  color?: string;
  role?: string;
  display?: string;
  alternative_of?: unknown;
}

const FOV = 30;
const DRAG_PX = 4;

/** An `overlay` as the mock holds it: values `[frame][key]` sampled at the current time. */
export interface MockOverlay {
  keys: Map<string, number>;
  times: number[];
  values: Float32Array;
  stops: Stops;
  range: [number, number];
  opacity: number;
}

/** An overlay's value for key k at time t: linear between frames, clamped to the ends. */
export function overlayValue(o: Pick<MockOverlay, 'times' | 'values' | 'keys'>, k: number, t: number): number {
  const n = o.times.length;
  const K = o.keys.size;
  if (n <= 1 || t <= o.times[0]!) return o.values[k]!;
  if (t >= o.times[n - 1]!) return o.values[(n - 1) * K + k]!;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (o.times[mid]! <= t) lo = mid;
    else hi = mid;
  }
  const w = (t - o.times[lo]!) / (o.times[hi]! - o.times[lo]!);
  const a = o.values[lo * K + k]!;
  return a + (o.values[hi * K + k]! - a) * w;
}

/** The colour an overlay gives a value, mixed over the base colour by opacity; null for NaN. */
export function overlayColour(o: Pick<MockOverlay, 'stops' | 'range' | 'opacity'>, v: number, base: string): string | null {
  if (!Number.isFinite(v)) return null;
  const [lo, hi] = o.range;
  const c = sampleStops(o.stops, hi > lo ? (v - lo) / (hi - lo) : 0);
  if (o.opacity >= 1) return `rgb(${c[0]},${c[1]},${c[2]})`;
  const b = hexRgb(base.startsWith('#') ? base : '#b8a89c');
  const m = (i: 0 | 1 | 2) => Math.round(b[i] + (c[i] - b[i]) * o.opacity);
  return `rgb(${m(0)},${m(1)},${m(2)})`;
}

/** POSITION min/max from a GLB's JSON chunk (glTF requires them on POSITION accessors). */
async function glbBounds(url: string): Promise<{ min: Vec3; max: Vec3 } | null> {
  const res = await fetch(url);
  if (!res.ok) return null;
  const buf = await res.arrayBuffer();
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== 0x46546c67) return null;
  const len = dv.getUint32(12, true);
  const json = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 20, len))) as {
    meshes?: { primitives: { attributes: { POSITION?: number } }[] }[];
    accessors?: { min?: number[]; max?: number[] }[];
  };
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const m of json.meshes ?? [])
    for (const p of m.primitives) {
      const a = p.attributes.POSITION !== undefined ? json.accessors?.[p.attributes.POSITION] : undefined;
      if (!a?.min || !a.max) continue;
      for (let k = 0; k < 3; k++) {
        min[k] = Math.min(min[k]!, a.min[k]!);
        max[k] = Math.max(max[k]!, a.max[k]!);
      }
    }
  return Number.isFinite(min[0]) ? { min, max } : null;
}

export class MockViewport implements Viewport {
  private ctx: CanvasRenderingContext2D;
  private items: Item[] = [];
  private byId = new Map<string, Item>();
  /** Modes set by system, context or all targets. */
  private modes = new Map<string, Mode>();
  /** Modes set by id: these win over isolation, as an explicit per-structure choice. */
  private idModes = new Map<string, Mode>();
  private defaults = new Map<string, Mode>();
  private isolate: Set<string> | null = null;
  private isolateOutside: 'ghost' | 'hidden' = 'ghost';
  private selected: string | null = null;
  private uiHover: string | null = null;
  private pointerHover: string | null = null;
  private matches: Set<string> | null = null;
  private labelIds: string[] = [];
  private insets = { top: 0, right: 0, bottom: 0, left: 0 };
  private orbit: Orbit = { targetMm: [0, 0, 0], yawDeg: PRESETS.hero.yawDeg, pitchDeg: PRESETS.hero.pitchDeg, distanceMm: 400 };
  private anim: { from: Orbit; to: Orbit; t0: number; ms: number } | null = null;
  private bounds: { min: Vec3; max: Vec3 } = { min: [-70, -100, -60], max: [70, 70, 80] };
  private viewProj: Mat4 = new Float32Array(16);
  private dirty = true;
  private raf = 0;
  private ro: ResizeObserver;
  private size = { w: 1, h: 1, dpr: 1 };
  private drag: { x: number; y: number; moved: boolean; button: number; orbit: Orbit; pan: boolean } | null = null;
  private frames = 0;
  private lastStats = performance.now();
  private disposed = false;
  private time = 0;
  private overlays = new Map<string, MockOverlay>();
  private sims = new Map<string, string>();
  private simLayers = new Map<string, MockLayer & { slot: string }>();
  /** The API version reported: 0.3 only when asked for (`?mockApi=0.3`), so 0.3 commands reach it only then. */
  private api: '0.2' | '0.3' = '0.2';
  /** At 0.3, the sim layers it draws (`ready.layers`): the engine's 0.3 list (#576). Sim volumes and surfaces aren't in it. */
  static readonly LAYERS_0_3 = ['vessels', 'particles', 'activity', 'arbors', 'somata', 'synapses'];
  private cells = new Map<string, MockCells>();
  /** 0.3's glows, at most 4: a soft additive disc over what's drawn, `radiusMm` at its point's depth. */
  private glows = new Map<string, { at: Vec3; radiusMm: number; color: string; opacity: number }>();
  /** A pack has loaded: `sim` needs one (contract v0.2). */
  private packLoaded = false;

  /** The frame painted for the still at one framing, reused until the framing changes. */
  private stillFrame: { key: string; canvas: HTMLCanvasElement } | null = null;

  private constructor(
    private canvas: HTMLCanvasElement,
    private opts: ViewportOptions,
    /** A still picture and its camera in place of the ellipses (scripts/parity.ts). */
    private still: { spec: StillSpec; plate: Plate } | null = null,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas unavailable');
    this.ctx = ctx;
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(canvas);
    this.resize();
    this.listen();
    this.loop();
  }

  static async create(canvas: HTMLCanvasElement, opts: ViewportOptions): Promise<MockViewport> {
    const spec = window.__baStill;
    const still = spec ? { spec, plate: await loadPlate(spec.plate) } : null;
    const vp = new MockViewport(canvas, opts, still);
    vp.api = new URLSearchParams(location.search).get('mockApi') === '0.3' ? '0.3' : '0.2';
    const layers = vp.api === '0.3' ? { layers: [...MockViewport.LAYERS_0_3] } : {};
    queueMicrotask(() => opts.onEvent({ ev: 'ready', api: vp.api, engine: 'mock', adapter: 'Canvas 2D (mock viewport)', packs: ['anatomy'], ...layers }));
    return vp;
  }

  private emit(e: ViewportEvent) {
    if (!this.disposed) this.opts.onEvent(e);
  }

  async send<C extends Command>(cmd: C): Promise<Reply<C>> {
    const r = await this.apply(cmd);
    this.dirty = true;
    return r as Reply<C>;
  }

  setTime(seconds: number) {
    if (seconds === this.time) return;
    this.time = seconds;
    if (this.overlays.size || this.cells.size) this.dirty = true;
  }

  /** The colour overlays give a structure now, the last-added overlay winning; null when none keys it. */
  private overlayFill(i: Item): string | null {
    let fill: string | null = null;
    for (const o of this.overlays.values()) {
      const k = o.keys.get(i.id);
      if (k === undefined) continue;
      fill = overlayColour(o, overlayValue(o, k, this.time), i.colour) ?? fill;
    }
    return fill;
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.ro.disconnect();
  }

  private resolve(t: Target): { ids: string[]; unknown: string[] } {
    const unknown: string[] = [];
    let ids: string[] = [];
    const under = (root: string) => this.items.filter((i) => i.id === root || this.isUnder(i, root)).map((i) => i.id);
    if ('all' in t) ids = this.items.map((i) => i.id);
    else if ('context' in t) ids = this.items.filter((i) => i.context).map((i) => i.id);
    else if ('system' in t) ids = this.items.filter((i) => i.system === t.system && !i.context).map((i) => i.id);
    else
      for (const id of 'id' in t ? [t.id] : t.ids) {
        const found = under(id);
        if (found.length) ids.push(...found);
        else unknown.push(id);
      }
    return { ids, unknown };
  }

  private parents = new Map<string, string | null>();
  private isUnder(i: Item, root: string): boolean {
    let p = i.parent;
    for (let guard = 0; p && guard < 32; guard++) {
      if (p === root) return true;
      p = this.parents.get(p) ?? null;
    }
    return false;
  }

  private async apply(cmd: Command): Promise<unknown> {
    switch (cmd.cmd) {
      case 'load':
        return this.load(cmd.pack);
      case 'visibility': {
        const unknown: string[] = [];
        for (const { target, mode } of cmd.set) {
          const r = this.resolve(target);
          unknown.push(...r.unknown);
          const byId = 'id' in target || 'ids' in target;
          for (const id of r.ids) {
            if (byId) {
              if (mode === 'default') this.idModes.delete(id);
              else this.idModes.set(id, mode);
            } else {
              this.modes.set(id, mode === 'default' ? this.defaults.get(id) ?? 'shown' : mode);
              if ('all' in target && mode === 'default') this.idModes.clear();
            }
          }
        }
        return { unknown };
      }
      case 'isolate': {
        if (!cmd.target) {
          this.isolate = null;
          return { unknown: [] };
        }
        const r = this.resolve(cmd.target);
        this.isolate = new Set(r.ids);
        this.isolateOutside = cmd.outside ?? 'ghost';
        return { unknown: r.unknown };
      }
      case 'select':
        this.selected = cmd.id && this.byId.has(cmd.id) ? cmd.id : null;
        return { unknown: cmd.id && !this.byId.has(cmd.id) ? [cmd.id] : [] };
      case 'emphasis':
        if (cmd.hover !== undefined) this.uiHover = cmd.hover;
        if (cmd.matches !== undefined) this.matches = cmd.matches ? new Set(cmd.matches) : null;
        return { unknown: [] };
      case 'camera':
        return { orbit: this.camera(cmd) };
      case 'insets':
        this.insets = { top: cmd.top, right: cmd.right, bottom: cmd.bottom, left: cmd.left };
        this.updateCamera();
        return {};
      case 'labels':
        this.labelIds = cmd.ids.slice(0, 256);
        return { unknown: this.labelIds.filter((id) => !this.byId.has(id)) };
      case 'section':
      case 'explode':
      case 'look':
        return {};
      case 'layer':
        if (this.api === '0.3' && !MockViewport.LAYERS_0_3.includes(cmd.name)) throw new Error(`layer ${cmd.name}: this viewport doesn't draw it`);
        this.simLayers.set(`${cmd.slot ?? 'main'}:${cmd.name}`, { on: cmd.on, slot: cmd.slot ?? 'main', quantity: cmd.quantity, colormap: cmd.colormap, range: cmd.range });
        return {};
      case 'overlay': {
        if ('remove' in cmd) {
          this.overlays.delete(cmd.id);
          return { unknown: [] };
        }
        if (cmd.values.length !== cmd.times.length * cmd.keys.length) throw new Error(`overlay ${cmd.id}: ${cmd.values.length} values for ${cmd.times.length} times × ${cmd.keys.length} keys`);
        this.overlays.delete(cmd.id);
        this.overlays.set(cmd.id, {
          keys: new Map(cmd.keys.map((k, i) => [k, i])),
          times: cmd.times,
          values: cmd.values,
          stops: colormapStops(cmd.colormap as Colormap),
          range: cmd.range,
          opacity: cmd.opacity ?? 1,
        });
        return { unknown: cmd.keys.filter((k) => !this.byId.has(k)) };
      }
      case 'sim': {
        const slot = cmd.slot ?? 'main';
        this.cells.delete(slot);
        if (cmd.path === null) {
          this.sims.delete(slot);
          for (const key of [...this.simLayers.keys()]) if (key.startsWith(`${slot}:`)) this.simLayers.delete(key);
          return { header: null };
        }
        if (!this.packLoaded) throw new Error(`sim ${cmd.path}: load a pack first`);
        const res = await fetch(`${this.opts.assets}sims/${cmd.path}/header.json`);
        if (!res.ok) throw new Error(`sims/${cmd.path}/header.json: HTTP ${res.status}`);
        const header: unknown = await res.json();
        if (this.api === '0.3' && (header as { format?: string }).format === 'ba-cells-sim-v0') this.cells.set(slot, await MockCells.open(this.opts.assets, cmd.path, header as CellsHeader));
        // One progress step: the mock reads the header (and, at 0.3, a cells run's arrays).
        this.emit({ ev: 'progress', loaded: 1, total: 1, of: 'sim', slot });
        this.sims.set(slot, cmd.path);
        this.emit({ ev: 'loaded', id: cmd.path, slot, header });
        return { header };
      }
      case 'glow': {
        if (this.api !== '0.3') throw new Error('unknown command glow');
        if ('remove' in cmd) {
          this.glows.delete(cmd.id);
          this.dirty = true;
          return { unknown: [] };
        }
        const at = typeof cmd.at === 'string' ? this.byId.get(cmd.at)?.centre : cmd.at;
        if (!at) return { unknown: [cmd.at as string] };
        if (!this.glows.has(cmd.id) && this.glows.size >= 4) throw new Error(`glow ${cmd.id}: at most 4 glows at once`);
        this.glows.set(cmd.id, { at: [...at] as Vec3, radiusMm: cmd.radiusMm, color: cmd.color, opacity: cmd.opacity ?? 1 });
        this.dirty = true;
        return { unknown: [] };
      }
      case 'capture': {
        // The canvas as it is, scaled to the size asked for; the engine's limits (contract v0.2).
        const w = cmd.width ?? this.canvas.width, h = cmd.height ?? this.canvas.height;
        if (!(Number.isInteger(w) && Number.isInteger(h) && w > 0 && h > 0)) throw new Error(`capture: ${w}×${h} isn't a whole number of pixels`);
        if (w * h > 4096 * 4096) throw new Error(`capture: ${w}×${h} is over 16.8 Mpx`);
        let src = this.canvas;
        if (w !== this.canvas.width || h !== this.canvas.height) {
          src = document.createElement('canvas');
          src.width = w;
          src.height = h;
          src.getContext('2d')?.drawImage(this.canvas, 0, 0, w, h);
        }
        const png = await new Promise<Blob>((ok, err) => src.toBlob((b) => (b ? ok(b) : err(new Error('capture failed'))), 'image/png'));
        return { png };
      }
      default:
        throw new Error(`unknown command ${(cmd as { cmd: string }).cmd}`);
    }
  }

  /** A pack's structures and the packs' it extends, bases first, each with the directory its meshes are in. */
  private async chain(pack: string, seen: Set<string> = new Set()): Promise<{ dir: string; s: RawStructure }[]> {
    seen.add(pack);
    const dir = `${this.opts.assets}packs/${pack}/`;
    // A connectome pack has no structures.json; asking for one would only log a 404.
    const meta = (await fetch(`${dir}pack.json`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)) as { contract?: string; extends?: { id: string }[] } | null;
    if (meta?.contract === 'connectome-pack-v0') throw new Error(`${pack}: the mock viewport loads anatomy packs only`);
    const out: { dir: string; s: RawStructure }[] = [];
    for (const ref of meta?.extends ?? []) if (!seen.has(ref.id)) out.push(...(await this.chain(ref.id, seen)));
    const res = await fetch(`${dir}structures.json`);
    if (!res.ok) throw new Error(`${dir}structures.json: HTTP ${res.status}`);
    const raw = (await res.json()) as RawStructure[] | { structures: RawStructure[] };
    const ids = new Set(out.map((x) => x.s.id));
    for (const s of Array.isArray(raw) ? raw : raw.structures) {
      if (ids.has(s.id) || (meta?.extends?.length && Array.isArray(s.alternative_of))) continue;
      ids.add(s.id);
      out.push({ dir, s });
    }
    return out;
  }

  private async load(pack: string) {
    const entries = await this.chain(pack);
    const structures = entries.map((x) => x.s);
    this.parents = new Map(structures.map((s) => [s.id, s.parent]));
    const drawable = entries.filter((x) => x.s.mesh);
    let done = 0;
    const items = await Promise.all(
      drawable.map(async ({ dir, s }) => {
        const b = await glbBounds(dir + s.mesh).catch(() => null);
        this.emit({ ev: 'progress', loaded: ++done, total: drawable.length, of: 'pack' });
        if (!b) return null;
        const centre: Vec3 = [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];
        const radius = Math.hypot(b.max[0] - b.min[0], b.max[1] - b.min[1], b.max[2] - b.min[2]) / 2;
        const context = s.role === 'context' || s.display === 'context';
        return { id: s.id, system: s.system, parent: s.parent, context, colour: s.color ?? '#b8a89c', min: b.min, max: b.max, centre, radius } satisfies Item;
      }),
    );
    this.items = items.filter((i): i is Item => i !== null);
    this.byId = new Map(this.items.map((i) => [i.id, i]));
    this.defaults = new Map(this.items.map((i) => [i.id, i.context ? 'ghost' : 'shown'] as const));
    this.modes = new Map(this.defaults);
    this.packLoaded = true;
    this.idModes.clear();
    this.isolate = null;
    this.selected = null;
    this.matches = null;
    const min: Vec3 = [Infinity, Infinity, Infinity];
    const max: Vec3 = [-Infinity, -Infinity, -Infinity];
    for (const i of this.items)
      for (let k = 0; k < 3; k++) {
        min[k] = Math.min(min[k]!, i.min[k]!);
        max[k] = Math.max(max[k]!, i.max[k]!);
      }
    if (Number.isFinite(min[0])) this.bounds = { min, max };
    this.camera({ cmd: 'camera', preset: 'hero', transitionMs: 0 });
    const reply = {
      kind: 'anatomy' as const,
      structures: structures.map((s) => ({ id: s.id, name: s.id, system: s.system, parent: s.parent, provenance: 'unknown', ...(s.role ? { role: s.role } : {}) })),
      boundsMm: [min, max] as [Vec3, Vec3],
      issues: [] as string[],
    };
    this.emit({ ev: 'loaded', id: pack, ...reply });
    return reply;
  }

  private fit(min: Vec3, max: Vec3, yawDeg: number, pitchDeg: number): Orbit {
    const centre: Vec3 = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    const r = Math.max(1, Math.hypot(max[0] - min[0], max[1] - min[1], max[2] - min[2]) / 2);
    const { w, h } = this.size;
    const clearW = Math.max(1, w - this.insets.left - this.insets.right);
    const clearH = Math.max(1, h - this.insets.top - this.insets.bottom);
    // The bounding sphere fits the clear area: the half-angles it leaves vertically and horizontally.
    const t = Math.tan((FOV * Math.PI) / 360);
    const half = Math.min(Math.atan((t * clearH) / h), Math.atan((t * clearW) / h));
    return { targetMm: centre, yawDeg, pitchDeg, distanceMm: (r / Math.sin(half)) * 1.04 };
  }

  private camera(cmd: Extract<Command, { cmd: 'camera' }>): Orbit {
    // A still has one camera: the plate's.
    if (this.still) return this.orbit;
    let to: Orbit = this.orbit;
    const dir = PRESETS[cmd.direction ?? cmd.preset ?? 'hero'];
    if (cmd.orbit) to = cmd.orbit;
    else if (cmd.frame) {
      const ids = cmd.frame === 'visible' ? this.items.filter((i) => this.modeOf(i) === 'shown').map((i) => i.id) : this.resolve(cmd.frame).ids;
      const sel = ids.map((id) => this.byId.get(id)).filter((i): i is Item => !!i);
      if (sel.length) {
        const min: Vec3 = [Infinity, Infinity, Infinity];
        const max: Vec3 = [-Infinity, -Infinity, -Infinity];
        for (const i of sel)
          for (let k = 0; k < 3; k++) {
            min[k] = Math.min(min[k]!, i.min[k]!);
            max[k] = Math.max(max[k]!, i.max[k]!);
          }
        to = this.fit(min, max, cmd.direction || cmd.preset ? dir.yawDeg : this.orbit.yawDeg, cmd.direction || cmd.preset ? dir.pitchDeg : this.orbit.pitchDeg);
      }
    } else if (cmd.preset) to = this.fit(this.bounds.min, this.bounds.max, dir.yawDeg, dir.pitchDeg);
    const ms = this.opts.reducedMotion ? 0 : cmd.transitionMs ?? 600;
    if (ms > 0) this.anim = { from: { ...this.orbit }, to, t0: performance.now(), ms };
    else {
      this.anim = null;
      this.orbit = to;
      this.updateCamera();
    }
    return to;
  }

  private modeOf(i: Item): Mode {
    const own = this.idModes.get(i.id);
    if (own === 'hidden') return own;
    let m = own ?? this.modes.get(i.id) ?? 'shown';
    // An isolation shows its members whatever their layer says; a hidden non-member stays hidden.
    if (this.isolate?.has(i.id) && !own) m = 'shown';
    if (m === 'hidden') return m;
    if (this.isolate && !this.isolate.has(i.id)) m = i.context ? m : this.isolateOutside;
    if (this.matches && !this.matches.has(i.id) && m === 'shown') m = 'ghost';
    return m;
  }

  private resize() {
    const r = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, this.opts.maxDpr ?? 2);
    this.size = { w: Math.max(1, r.width), h: Math.max(1, r.height), dpr };
    this.canvas.width = Math.round(this.size.w * dpr);
    this.canvas.height = Math.round(this.size.h * dpr);
    this.updateCamera();
  }

  /** The still's framing: its zoom about the clear area's centre (the insets as a lens shift). */
  private framing(): Framing {
    const { w, h } = this.size;
    const i = this.still?.spec.insets ?? this.insets;
    return { s: this.still?.spec.zoom ?? 1, cx: (i.left + (w - i.right)) / 2, cy: (i.top + (h - i.bottom)) / 2 };
  }

  private updateCamera() {
    const { w, h } = this.size;
    if (this.still) {
      const { plate } = this.still;
      const cam = stillCamera(plate, this.framing(), w, h);
      const d = plate.d;
      this.orbit = { targetMm: plate.targetMm, yawDeg: (Math.atan2(d[0], d[1]) * 180) / Math.PI, pitchDeg: (Math.asin(d[2]) * 180) / Math.PI, distanceMm: plate.distanceMm };
      this.viewProj = cam.viewProj;
      this.dirty = true;
      this.emit({ ev: 'camera', orbit: { ...this.orbit }, eyeMm: cam.eye, fovYDeg: cam.fovYDeg, viewProj: this.viewProj, width: w, height: h });
      return;
    }
    const eye = eyeOf(this.orbit);
    const view = lookAt(eye, this.orbit.targetMm, UP);
    const proj = perspective(FOV, w / h, 1, 5000);
    // Lens shift: move the image centre to the clear area's centre by (cx, cy) in NDC. Clip w is
    // −z_view, so x_ndc + cx needs clip.x + cx·(−z_view): the z column carries −cx.
    const cx = (this.insets.left - this.insets.right) / w;
    const cy = (this.insets.bottom - this.insets.top) / h;
    proj[8] = -cx;
    proj[9] = -cy;
    this.viewProj = multiply(proj, view);
    this.dirty = true;
    this.emit({ ev: 'camera', orbit: { ...this.orbit }, eyeMm: eye, fovYDeg: FOV, viewProj: this.viewProj, width: w, height: h });
  }

  private pick(x: number, y: number): { id: string | null; mm: Vec3 | null } {
    let best: { id: string; depth: number; mm: Vec3 } | null = null;
    for (const i of this.items) {
      const m = this.modeOf(i);
      if (m !== 'shown' || i.context) continue;
      const p = project(this.viewProj, i.centre, this.size.w, this.size.h);
      if (p.behind) continue;
      const rpx = this.radiusPx(i);
      if (Math.hypot(x - p.x, y - p.y) <= rpx * 0.8 && (!best || p.depth < best.depth)) best = { id: i.id, depth: p.depth, mm: i.centre };
    }
    return best ? { id: best.id, mm: best.mm } : { id: null, mm: null };
  }

  private radiusPx(i: Item): number {
    const d = Math.max(1, Math.hypot(...(eyeOf(this.orbit).map((e, k) => e - i.centre[k]!) as Vec3)));
    return (i.radius / d) * (this.size.h / 2) / Math.tan((FOV * Math.PI) / 360) * 0.55;
  }

  private listen() {
    const c = this.canvas;
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    c.addEventListener('pointerdown', (e) => {
      c.setPointerCapture(e.pointerId);
      this.drag = { x: e.offsetX, y: e.offsetY, moved: false, button: e.button, orbit: { ...this.orbit }, pan: e.button === 2 || e.shiftKey };
    });
    c.addEventListener('pointermove', (e) => {
      if (this.drag) {
        const dx = e.offsetX - this.drag.x, dy = e.offsetY - this.drag.y;
        if (Math.hypot(dx, dy) >= DRAG_PX) this.drag.moved = true;
        if (!this.drag.moved) return;
        this.anim = null;
        const o = this.drag.orbit;
        if (this.drag.pan) {
          const s = o.distanceMm / this.size.h;
          const y = (o.yawDeg * Math.PI) / 180;
          const right: Vec3 = [Math.cos(y), -Math.sin(y), 0];
          this.orbit = { ...o, targetMm: [o.targetMm[0] - right[0] * dx * s, o.targetMm[1] - right[1] * dx * s, o.targetMm[2] + dy * s] };
        } else this.orbit = { ...o, yawDeg: o.yawDeg + dx * 0.3, pitchDeg: Math.max(-89, Math.min(89, o.pitchDeg + dy * 0.3)) };
        this.updateCamera();
        return;
      }
      const hit = this.pick(e.offsetX, e.offsetY);
      if (hit.id !== this.pointerHover) {
        this.pointerHover = hit.id;
        this.dirty = true;
        this.emit({ ev: 'hover', id: hit.id, x: e.offsetX, y: e.offsetY, mm: hit.mm });
      }
    });
    c.addEventListener('pointerleave', () => {
      if (this.pointerHover !== null) {
        this.pointerHover = null;
        this.dirty = true;
        this.emit({ ev: 'hover', id: null, x: -1, y: -1, mm: null });
      }
    });
    c.addEventListener('pointerup', (e) => {
      const d = this.drag;
      this.drag = null;
      if (!d || d.moved) return;
      const hit = this.pick(e.offsetX, e.offsetY);
      this.emit({ ev: 'click', id: hit.id, x: e.offsetX, y: e.offsetY, mm: hit.mm, button: e.button, count: e.detail >= 2 ? 2 : 1, mods: { shift: e.shiftKey, alt: e.altKey, ctrl: e.ctrlKey, meta: e.metaKey } });
    });
    c.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        this.anim = null;
        this.orbit = { ...this.orbit, distanceMm: Math.max(20, Math.min(4000, this.orbit.distanceMm * Math.exp(e.deltaY * 0.0015))) };
        this.updateCamera();
      },
      { passive: false },
    );
  }

  private loop = () => {
    this.raf = requestAnimationFrame(this.loop);
    const now = performance.now();
    if (this.anim) {
      const t = Math.min(1, (now - this.anim.t0) / this.anim.ms);
      const k = 1 - Math.pow(1 - t, 3);
      const { from, to } = this.anim;
      let dy = to.yawDeg - from.yawDeg;
      dy = ((dy + 540) % 360) - 180;
      this.orbit = {
        targetMm: [0, 1, 2].map((i) => from.targetMm[i]! + (to.targetMm[i]! - from.targetMm[i]!) * k) as Vec3,
        yawDeg: from.yawDeg + dy * k,
        pitchDeg: from.pitchDeg + (to.pitchDeg - from.pitchDeg) * k,
        distanceMm: from.distanceMm + (to.distanceMm - from.distanceMm) * k,
      };
      if (t >= 1) this.anim = null;
      this.updateCamera();
    }
    if (this.dirty) {
      this.dirty = false;
      this.draw();
      this.frames++;
    }
    if (now - this.lastStats > 1000) {
      this.emit({ ev: 'stats', fps: (this.frames * 1000) / (now - this.lastStats), frameMs: 0 });
      this.frames = 0;
      this.lastStats = now;
    }
  };

  private drawStill(still: { spec: StillSpec; plate: Plate }) {
    const { w, h, dpr } = this.size;
    const fr = this.framing();
    const key = [w, h, dpr, fr.s, fr.cx, fr.cy].join(',');
    if (this.stillFrame?.key !== key) {
      const c = document.createElement('canvas');
      c.width = this.canvas.width;
      c.height = this.canvas.height;
      const cx = c.getContext('2d')!;
      paintPlate(cx, still.plate, fr, w, h, dpr);
      if (still.spec.paint) paintSurface(cx, still.plate, fr, still.spec.paint, w, h, dpr);
      this.stillFrame = { key, canvas: c };
    }
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.drawImage(this.stillFrame.canvas, 0, 0);
    this.emitLabels();
  }

  private draw() {
    if (this.still) return this.drawStill(this.still);
    const { ctx } = this;
    const { w, h, dpr } = this.size;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const g = ctx.createRadialGradient(w / 2, h * 0.45, 0, w / 2, h * 0.45, Math.max(w, h) * 0.7);
    g.addColorStop(0, '#15181d');
    g.addColorStop(1, '#07080a');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    const drawn = this.items
      .map((i) => ({ i, m: this.modeOf(i), p: project(this.viewProj, i.centre, w, h) }))
      .filter((d) => d.m !== 'hidden' && !d.p.behind)
      .sort((a, b) => b.p.depth - a.p.depth);
    for (const { i, m, p } of drawn) {
      const r = this.radiusPx(i);
      const hot = i.id === this.pointerHover || i.id === this.uiHover;
      const fill = m === 'ghost' ? null : this.overlayFill(i);
      ctx.globalAlpha = m === 'ghost' ? 0.07 : hot ? 0.95 : fill ? 0.9 : 0.78;
      const base = fill ?? i.colour;
      const grad = ctx.createRadialGradient(p.x - r * 0.3, p.y - r * 0.35, r * 0.1, p.x, p.y, r);
      grad.addColorStop(0, hot ? '#ffffff' : lighten(base, fill ? 0.2 : 0.35));
      grad.addColorStop(1, base);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
      if (i.id === this.selected) {
        ctx.globalAlpha = 1;
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#7cc4ff';
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
    if (this.glows.size) {
      ctx.globalCompositeOperation = 'lighter';
      for (const g of this.glows.values()) {
        const p = project(this.viewProj, g.at, w, h);
        if (p.behind) continue;
        const d = Math.max(1, Math.hypot(...(eyeOf(this.orbit).map((e, k) => e - g.at[k]!) as Vec3)));
        const r = ((g.radiusMm / d) * (h / 2)) / Math.tan((FOV * Math.PI) / 360);
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
        grad.addColorStop(0, g.color);
        grad.addColorStop(1, 'transparent');
        ctx.globalAlpha = g.opacity;
        ctx.fillStyle = grad;
        ctx.fillRect(p.x - r, p.y - r, 2 * r, 2 * r);
      }
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    }
    for (const [slot, c] of this.cells) c.draw(ctx, this.viewProj, w, h, this.time, (name) => this.simLayers.get(`${slot}:${name}`), () => (this.dirty = true));
    this.emitLabels();
  }

  private emitLabels() {
    if (!this.labelIds.length) return;
    const out = new Float32Array(this.labelIds.length * ANCHOR_STRIDE);
    if (this.still) {
      // Only the still's anchors are on screen; every other structure has no pixels.
      const anchors = new Map(this.still.spec.anchors.map((a) => [a.id, a]));
      this.labelIds.forEach((id, k) => {
        const a = anchors.get(id);
        if (!a) return;
        const p = project(this.viewProj, a.at, this.size.w, this.size.h);
        const inView = p.x >= 0 && p.y >= 0 && p.x <= this.size.w && p.y <= this.size.h;
        out.set([p.x, p.y, p.depth, a.area, (a.visible === false ? 0 : ANCHOR_VISIBLE) | (inView ? ANCHOR_IN_VIEW : 0) | (p.behind ? ANCHOR_BEHIND : 0)], k * ANCHOR_STRIDE);
      });
      this.emit({ ev: 'labels', anchors: out });
      return;
    }
    this.labelIds.forEach((id, k) => {
      const i = this.byId.get(id);
      if (!i) return;
      const p = project(this.viewProj, i.centre, this.size.w, this.size.h);
      const visible = this.modeOf(i) === 'shown';
      const inView = p.x >= 0 && p.y >= 0 && p.x <= this.size.w && p.y <= this.size.h;
      const r = this.radiusPx(i);
      out.set([p.x, p.y, p.depth, Math.PI * r * r, (visible ? ANCHOR_VISIBLE : 0) | (inView ? ANCHOR_IN_VIEW : 0) | (p.behind ? ANCHOR_BEHIND : 0)], k * ANCHOR_STRIDE);
    });
    this.emit({ ev: 'labels', anchors: out });
  }
}

function lighten(colour: string, t: number): string {
  const rgb = /^rgb\((\d+),(\d+),(\d+)\)$/.exec(colour);
  const n = rgb ? (Number(rgb[1]) << 16) | (Number(rgb[2]) << 8) | Number(rgb[3]) : parseInt(colour.slice(1), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}
